import React from 'react';
import { Card, Collapse, Form, Input, Select, ConfigProvider } from 'antd';
import { Typography } from '@hulu/react-style-components';
import { IconClose } from '@hulu-react-style-components/icons';

import { AuctionErrorBoundary } from '../AuctionErrorBoundary';
import { TIME_FRAMES } from '../../../constants';
import {
  StyledDrawer,
  StyledCloseIconContainer,
  StyledContentWrapper,
  StyledPaneFooter,
  StyledCancelButton,
  StyledSubmitButton,
  StyledHeadItemWrapper,
  StyledFooterWrapper,
} from './styles';
import { useAuctionSnapshotCreateForm } from './useAuctionSnapshotCreateForm';
import { FORM_FIELDS, SNAPSHOT_NAME_MAX_LENGTH } from '../constants';
import {
  USER_PARAMETERS_KEY,
  APP_PARAMETERS_KEY,
  CONTENT_PARAMETERS_KEY,
  DEMAND_PARAMETERS_KEY,
  SNAPSHOT_NAME_PREFIX_WARNING,
} from './constants';
import { DeviceParameters, ContentParameters, UserParametersSection, DemandParameters } from './SnapshotFormSections';
import { CAS } from '../AuctionSnapshotDetailedView/constants';

const { Option } = Select;
const { Panel } = Collapse;

const snapshotNameStyle = { margin: 0 };
const BID_STATE_INDEX = 0;
const BID_DEAL_TYPE_INDEX = 0;
const POD_POSITION_INDEX = 0;
const ENTITY_TYPE_INDEX = 0;
const DISTRIBUTION_PARTNER_INDEX = 0;

const renderOptions = (itemName) => (
  <Option value={itemName} key={itemName}>
    {itemName}
  </Option>
);

export const AuctionSnapshotCreateForm = ({
  isVisible,
  closeAdRequestDrawer,
  onSnapshotsRefresh,
  selectedSnapshot,
  setSelectedSnapshot,
  snapshotParameters,
  profileTypes,
  snapshotCountriesParameters,
}) => {
  const {
    form,
    formInitialValues,
    creativeDurationSelect,
    profiles,
    isProfilesLoading,
    inventoryTypeValue,
    createSnapshotFormActiveKeys,
    winnerAuctionSelect,
    isInventoryTypeDisabled,
    isDeviceFamilyDisabled,
    isAdProductTypeDisabled,
    namePrefixWarning,
    isDspDisabled,

    closeForm,
    handleSubmitFailed,
    handleSubmit,
    handleChangeCreativeDuration,
    handleFormValuesChange,
    handleChangeProfile,
    handleChangeInventoryType,
    handleClearAppName,
    handleClearUserProfile,
    onCreateSnapshotFormCollapseChange,
    handleChangeWinnerAuction,
    handleProfilesFilter,
    handleChangeField,
    handleChangeAdProduct,
    handleChangeAdProductType,
    getOptionsByFieldName,
    handleChangeSelectType,
    handleBidStateFilterOption,
    clearNamePrefixWarning,
  } = useAuctionSnapshotCreateForm({
    closeAdRequestDrawer,
    onSnapshotsRefresh,
    selectedSnapshot,
    setSelectedSnapshot,
    snapshotParameters,
    snapshotCountriesParameters,
  });

  const supplySource = CAS;
  const publisher = form.getFieldValue(FORM_FIELDS.APP_NAME.name);
  const userProfile = form.getFieldValue(FORM_FIELDS.USER_PROFILE.name);
  const market = form.getFieldValue(FORM_FIELDS.MARKET.name);
  const bidStates = snapshotParameters?.snapshotSearchParametersByBidState?.[BID_STATE_INDEX]?.bidState;
  const bidDealTypes = snapshotParameters?.snapshotSearchParametersByDealType?.[BID_DEAL_TYPE_INDEX]?.dealType;
  const podPosition =
    snapshotParameters?.snapshotSearchParametersByPodPosition?.[POD_POSITION_INDEX]?.[FORM_FIELDS.POD_POSITION.name];
  const entityType =
    snapshotParameters?.snapshotSearchParametersByEntityType?.[ENTITY_TYPE_INDEX]?.[FORM_FIELDS.ENTITY_TYPE.name];
  const distributionPartner =
    snapshotParameters?.snapshotSearchParametersByDistributionPartner?.[DISTRIBUTION_PARTNER_INDEX]?.[
      FORM_FIELDS.DISTRIBUTION_PARTNER.name
    ];

  return (
    <StyledDrawer
      placement="right"
      open={isVisible}
      onClose={closeForm}
      title="Create Auction Snapshot"
      destroyOnClose
      width={600}
      closeIcon={
        <StyledCloseIconContainer>
          <IconClose />
        </StyledCloseIconContainer>
      }
      footer={
        <StyledPaneFooter>
          <StyledCancelButton id="snapshot-cancel-button" size="medium" onClick={closeForm}>
            <Typography variant="body3">Cancel</Typography>
          </StyledCancelButton>
          <StyledSubmitButton id="snapshot-capture-button" form="createAdRequestForm" htmlType="submit">
            <Typography variant="body3">Capture</Typography>
          </StyledSubmitButton>
        </StyledPaneFooter>
      }
    >
      <AuctionErrorBoundary>
        <StyledContentWrapper>
          <ConfigProvider getPopupContainer={(trigger) => trigger.parentNode}>
            <Form
              id="createAdRequestForm"
              name="createAdRequestForm"
              onFinish={handleSubmit}
              onFinishFailed={handleSubmitFailed}
              layout="vertical"
              form={form}
              initialValues={formInitialValues}
              onValuesChange={handleFormValuesChange}
            >
              <StyledHeadItemWrapper>
                <Form.Item
                  initialValue={`Drax_${Date.now()}`}
                  label={FORM_FIELDS.SNAPSHOT_NAME.label}
                  name={FORM_FIELDS.SNAPSHOT_NAME.name}
                  rules={[
                    { required: true, message: 'Snapshot Name is required!' },
                    {
                      message: SNAPSHOT_NAME_PREFIX_WARNING,
                      warningOnly: true,
                      validator: () => {
                        if (namePrefixWarning) {
                          return Promise.reject();
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  style={snapshotNameStyle}
                >
                  <Input maxLength={SNAPSHOT_NAME_MAX_LENGTH} showCount allowClear onChange={clearNamePrefixWarning} />
                </Form.Item>
              </StyledHeadItemWrapper>
              <Collapse onChange={onCreateSnapshotFormCollapseChange} defaultActiveKey={createSnapshotFormActiveKeys}>
                <Panel header="User Parameters" key={USER_PARAMETERS_KEY}>
                  <UserParametersSection
                    profileTypes={profileTypes}
                    handleChangeSelectType={handleChangeSelectType}
                    isProfilesLoading={isProfilesLoading}
                    isSelectTypeValue={form.getFieldValue(FORM_FIELDS.SELECT_TYPE.name)}
                    handleChangeProfile={handleChangeProfile}
                    handleProfilesFilter={handleProfilesFilter}
                    handleClearUserProfile={handleClearUserProfile}
                    profiles={profiles}
                    handleChangeField={handleChangeField}
                    getOptionsByFieldName={getOptionsByFieldName}
                    supplySource={supplySource}
                    handleClearAppName={handleClearAppName}
                    renderOptions={renderOptions}
                    userProfile={userProfile}
                    publisher={publisher}
                    podPosition={podPosition}
                    distributionPartner={distributionPartner}
                    market={market}
                    snapshotCountriesParameters={snapshotCountriesParameters}
                  />
                </Panel>
                <Panel header="Device Parameters" key={APP_PARAMETERS_KEY}>
                  <DeviceParameters
                    inventoryTypeValue={inventoryTypeValue}
                    supplySource={supplySource}
                    isInventoryTypeDisabled={isInventoryTypeDisabled}
                    handleChangeInventoryType={handleChangeInventoryType}
                    isDeviceFamilyDisabled={isDeviceFamilyDisabled}
                    handleChangeField={handleChangeField}
                    getOptionsByFieldName={getOptionsByFieldName}
                    renderOptions={renderOptions}
                  />
                </Panel>
                <Panel header="Content Parameters" key={CONTENT_PARAMETERS_KEY}>
                  <ContentParameters
                    supplySource={supplySource}
                    entityType={entityType}
                    handleChangeField={handleChangeField}
                    getOptionsByFieldName={getOptionsByFieldName}
                    renderOptions={renderOptions}
                  />
                </Panel>
                <Panel header="Demand Parameters" key={DEMAND_PARAMETERS_KEY}>
                  <DemandParameters
                    handleChangeCreativeDuration={handleChangeCreativeDuration}
                    creativeDurationSelect={creativeDurationSelect}
                    handleBidStateFilterOption={handleBidStateFilterOption}
                    bidStates={bidStates}
                    renderOptions={renderOptions}
                    handleChangeWinnerAuction={handleChangeWinnerAuction}
                    supplySource={supplySource}
                    getOptionsByFieldName={getOptionsByFieldName}
                    winnerAuctionSelect={winnerAuctionSelect}
                    isDspDisabled={isDspDisabled}
                    bidDealTypes={bidDealTypes}
                    isAdProductTypeDisabled={isAdProductTypeDisabled}
                    handleChangeAdProduct={handleChangeAdProduct}
                    handleChangeAdProductType={handleChangeAdProductType}
                  />
                </Panel>
              </Collapse>
              <StyledFooterWrapper>
                <Card>
                  <Form.Item label={FORM_FIELDS.TIME_ALLOWED.label} name={FORM_FIELDS.TIME_ALLOWED.name}>
                    <Select placeholder="Select" options={TIME_FRAMES} allowClear />
                  </Form.Item>
                </Card>
              </StyledFooterWrapper>
            </Form>
          </ConfigProvider>
        </StyledContentWrapper>
      </AuctionErrorBoundary>
    </StyledDrawer>
  );
};
